<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight"
    :embedded="embedded">

    <div class="row no-gutter h-100">
      <div class="col-3" v-if="showList">
        <div v-if="showDateFilter">
          <ReportFilterPopover
            headerTitle="NCR Filter"
            :showApplyButton="true"
            :showResetButton="true"
            :appendToBody="true"
            alignment="left"
            placement="bottom-end"
            @apply="$emit('dateApplied')"
            @reset="$emit('dateReset')">
            <template v-slot:popover>
              <ReportDateRangeSelect 
                :startDate="editingStartDate"
                :endDate="editingEndDate"
                @updateStartDate="editingStartDate = $event; $emit('updateEditingStartDate', $event)"
                @updateEndDate="editingEndDate = $event; $emit('updateEditingEndDate', $event)"
              />
            </template>
            <template v-slot:filterDisplay>
              <div>
                <i class="far fa-calendar mr-2"></i>{{startDate | formatDate}} - {{endDate | formatDate}}
              </div>                
            </template>
          </ReportFilterPopover>
        </div>
        <StaticDataTable class="static-table-list" v-if="hasChartData">
          <template v-slot:header>
            <tr>
              <th width="60%">{{ncrValueTableData.nameTitle}}</th>
              <th width="40%">{{ncrValueTableData.valueTitle}}</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="(dataValue, index) of ncrValueTableData.data" :key="index">
              <td>{{dataValue.name}}</td>
              <td><span v-html="dataValue.displayValue" /></td>
            </tr>
          </template>
        </StaticDataTable>
      </div>
      <div :class="showList && hasChartData ? 'col-9' : 'col-12'">
        <ChartDoughnut
          :series="ncrValueSeries"
          :legend="ncrValueLegend"
          :options="ncrValueOptions"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>



<script>
import _ from 'lodash'
import moment from 'moment'

import Utils from '@/utils/utils'
import AnalyticsDateMixin from '@/modules/intelligence/mixins/analyticsDate'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import ChartDoughnut from '@/components/charts/chartDoughnut'

import { ChartDateRangeTypes } from '@/modules/intelligence/types'

const NCROrderChartTypes = {
  ncrItemValue: 'ncrItemValue',
  ncrItemCount: 'ncrItemCount'
}

export default {
  name: 'NCROrderChartWidget',
  mixins: [AnalyticsDateMixin],
  components: {
    AnalyticsWidget,
    ChartDoughnut,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    filter: Object,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean,
    showList: Boolean,
    showDateFilter: Boolean,
    dateRangeType: {
      type: String,
      default: ChartDateRangeTypes.today
    },
    startDate: String,
    endDate: String
  },
  NCROrderChartTypes: NCROrderChartTypes,
  data() {
    return {
      ncrValueSeries: [],
      ncrValueTableData: {},
      ncrValueLegend: [],
      ncrValueOptions: {},
      editingStartDate: this.startDate,
      editingEndDate: this.endDate
    }
  },
  computed: {
    hasChartData() {
      if(this.ncrValueSeries) {
        return this.ncrValueSeries.some(series => {
          if(series.data && series.data.length > 0)
            return true
        })
      }
      return false
    }
  },
  mounted() {
    this.requestChartData()
  },
  watch: {
    dateRangeType: function() {
      this.requestChartData()
    },
    startDate: function() {
      this.editingStartDate = this.startDate
      this.requestChartData()
    },
    endDate: function() {
      this.editingEndDate = this.endDate
      this.requestChartData()
    },
    filter: function() {
      this.requestChartData()
    }
  },
  methods: {
    requestChartData() {
      var filterData = {
        startDate: this.calculateStartDateForDateRangeType(this.dateRangeType, this.startDate).toDate().toISOString(),
        endDate: this.calculateEndDateForDateRangeType(this.dateRangeType, this.endDate).toDate().toISOString(),
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null
      }

      this.$apiService.analyticsService.getNCRValueSummary(filterData)
      .then(response => {
        var ncrCauseList = response.data.ncrCauseList

        this.ncrValueOptions.tooltip = {
          formatter(param) {
            return `${param.seriesName} <br />${param.name} : ${Utils.formatCurrency(param.value)} (${param.percent}%)`
          }
        }

        if(this.type == NCROrderChartTypes.ncrItemValue) {
          this.ncrValueSeries = [{
            name: 'NCR Cause',
            data: ncrCauseList.map(causeInfo => {
              return {
                name: causeInfo.labelName,
                value: causeInfo.totalItemValue
              }
            }),
            label: {
              show: true,
              formatter(param) {
                return param.name
              }
            },
          }]
          this.ncrValueLegend = {
            data: ncrCauseList.map(causeInfo => causeInfo.labelName)
          }

          this.ncrValueTableData = {
            nameTitle: 'NCR Cause',
            valueTitle: 'Value',
            data: ncrCauseList.map(causeInfo => {
              return {
                name: causeInfo.labelName,
                value: causeInfo.totalItemValue,
                displayValue: Utils.formatCurrency(causeInfo.totalItemValue)
              }
            })
          }

          this.ncrValueTableData.data = _.sortBy(this.ncrValueTableData.data, 'value').reverse()
        }

        if(this.type == NCROrderChartTypes.ncrItemCount) {
          this.ncrValueOptions.tooltip = {
            formatter(param) {
              return `${param.seriesName} <br />${param.name} : ${Utils.formatInteger(param.value)} (${param.percent}%)`
            }
          }
          
          this.ncrValueSeries = [{
            name: 'Recuts',
            data: ncrCauseList.map(causeInfo => {
              return {
                name: causeInfo.labelName,
                value: causeInfo.itemCount
              }
            }),
            label: {
              show: true,
              formatter(param) {
                return param.name
              }
            },
          }]
          this.ncrValueLegend = {
            data: ncrCauseList.map(causeInfo => causeInfo.labelName)
          }

          this.ncrValueTableData = {
            nameTitle: 'NCR Cause',
            valueTitle: 'Value',
            data: ncrCauseList.map(causeInfo => {
              return {
                name: causeInfo.labelName,
                value: causeInfo.itemCount,
                displayValue: Utils.formatInteger(causeInfo.itemCount)
              }
            })
          }

          this.ncrValueTableData.data = _.sortBy(this.ncrValueTableData.data, 'value').reverse()
        }
      })
    }
  }
}


</script>